import React from 'react';
import { FiCheck, FiEdit2, FiSend, FiTrash2, FiClock } from 'react-icons/fi';
import { MindChatDraft } from '../services/MendsVisionService';
import { pdfPlanText } from '../lib/approvalLabels';

export type MindDraftStatus = 'draft' | 'editing' | 'sending' | 'queued' | 'sent' | 'error' | 'discarded';

interface MindDraftCardProps {
  draft: MindChatDraft;
  status: MindDraftStatus;
  /** Mensagem exibida abaixo do card: erro quando status = 'error', informativa nos demais. */
  message?: string;
  onTextChange: (text: string) => void;
  /** Alterna para o estado de edição; quem controla `status` é o pai. */
  onEdit: () => void;
  onQueue: () => void;
  onSendNow: () => void;
  onDiscard: () => void;
  /** Caixa "Enviar o PDF junto" (só com o plano `available`). Ausente = marcada. */
  includePdf?: boolean;
  onTogglePdf?: () => void;
}

const KIND_LABEL: Record<MindChatDraft['kind'], string> = {
  proposal: 'Proposta',
  follow_up: 'Follow-up',
};

/** Só nestes status os botões respondem; nos demais o card fica inerte (evita duplo clique). */
function isActionable(status: MindDraftStatus): boolean {
  return status === 'draft' || status === 'editing' || status === 'error';
}

export function MindDraftCard({
  draft,
  status,
  message,
  onTextChange,
  onEdit,
  onQueue,
  onSendNow,
  onDiscard,
  includePdf = true,
  onTogglePdf,
}: MindDraftCardProps) {
  if (status === 'discarded') {
    return <div className="mai-draft-discarded">Rascunho descartado</div>;
  }

  const kindLabel = KIND_LABEL[draft.kind] ?? 'Rascunho';
  const name = (draft.contactName ?? '').trim();
  const title = name ? `${kindLabel} para ${name}` : kindLabel;
  const text = draft.text ?? '';
  const hasText = text.trim().length > 0;
  const awaitingValue = draft.awaiting === 'value' && !hasText;

  if (awaitingValue) {
    return (
      <div className="mai-draft-card mai-draft-card-awaiting">
        <div className="mai-draft-title">{title}</div>
        <div className="mai-draft-question">
          <FiClock size={14} aria-hidden="true" /> Qual o valor da proposta? Responda no chat para eu montar o rascunho.
        </div>
      </div>
    );
  }

  const actionable = isActionable(status);
  const canSubmit = actionable && hasText;
  const pdfAvailable = draft.pdf?.status === 'available';
  // Desmarcado, o card não promete o PDF: fica só a caixa.
  const pdfLine = pdfAvailable && !includePdf ? null : pdfPlanText(draft.pdf);
  const facts = draft.usedFacts ?? [];
  const messageClass = status === 'error' ? 'mai-draft-message-error' : 'mai-draft-message-info';

  return (
    <div className="mai-draft-card">
      <div className="mai-draft-title">{title}</div>

      {status === 'editing' ? (
        <textarea
          className="mai-mind-edit-body mai-draft-textarea"
          aria-label="Texto do rascunho"
          value={text}
          rows={5}
          onChange={(e) => onTextChange(e.target.value)}
        />
      ) : (
        <div className="mai-draft-text">{text}</div>
      )}

      {facts.length > 0 && (
        <div className="mai-draft-facts">
          <div className="mai-draft-facts-title">Baseado em</div>
          <ul className="mai-draft-facts-list">
            {facts.map((fact, i) => (
              <li key={`${i}-${fact}`}>{fact}</li>
            ))}
          </ul>
        </div>
      )}

      {pdfLine && <div className="mai-draft-pdf">{pdfLine}</div>}
      {pdfAvailable && (
        <label className="mai-draft-pdf-toggle">
          <input
            type="checkbox"
            checked={includePdf}
            disabled={!actionable}
            onChange={() => actionable && onTogglePdf?.()}
          />
          {' '}Enviar o PDF junto
        </label>
      )}

      <div className="mai-btn-row mai-draft-actions">
        <button
          type="button"
          className="mai-btn mai-btn-soft"
          disabled={!actionable}
          aria-pressed={status === 'editing'}
          onClick={() => actionable && onEdit()}
        >
          <FiEdit2 size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
          Editar
        </button>
        <button
          type="button"
          className="mai-btn mai-btn-soft"
          disabled={!canSubmit}
          onClick={() => canSubmit && onQueue()}
        >
          <FiCheck size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
          Mandar para a fila
        </button>
        <button
          type="button"
          className={`mai-btn${draft.sendRequested ? ' mai-draft-send-highlight' : ''}`}
          disabled={!canSubmit}
          onClick={() => canSubmit && onSendNow()}
        >
          <FiSend size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
          Enviar agora
        </button>
        <button
          type="button"
          className="mai-btn mai-btn-ghost"
          disabled={!actionable}
          onClick={() => actionable && onDiscard()}
        >
          <FiTrash2 size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
          Descartar
        </button>
      </div>

      <div className="mai-draft-message-region" aria-live="polite">
        {message && <div className={messageClass}>{message}</div>}
      </div>
    </div>
  );
}

export default MindDraftCard;
